import { AttachInternals, Component, Prop, State, Event, EventEmitter, h, Watch } from '@stencil/core';

@Component({
  tag: 'spw-custom-select',
  styleUrl: 'spw-custom-select.scss',
  formAssociated: true,
  shadow: true,
})
export class SpwCustomSelect {
  private host: HTMLElement;
  private selectContainer: HTMLElement;

  /** Événement émis lorsque la valeur sélectionnée change */
  @Event() valueChanged: EventEmitter<(string | number)[] | string | number>;

  @State() internalValue: (string | number)[] | string | number;
  @State() isOpen: boolean = false;
  @State() parsedOptions: { value: string | number; label: string }[] = [];
  @State() focusedOptionIndex: number | null = null;
  @State() searchText: string = '';
  @State() dropdownDirection: 'up' | 'down' = 'down';
  @State() hasSelectedValue: boolean = false;

  /** Valeur actuelle sélectionnée dans le composant, peut être une chaîne, un nombre ou un tableau */
  @Prop({ mutable: true, reflect: true }) value: (string | number)[] | string | number = [];
  /** Label associé au champ de sélection personnalisé */
  @Prop() label: string = '';
  /** Placeholder à afficher lorsque aucune option n'est sélectionnée */
  @Prop() placeholder: string = 'Sélectionnez une option';
  /** Indique si le champ est désactivé et donc non interactif */
  @Prop() disabled: boolean;
  /** Nom de l'élément dans les formulaires, utilisé pour associer la valeur */
  @Prop() name: string;
  /** Indique si la sélection est obligatoire dans le formulaire */
  @Prop() required: boolean = false;
  /** Chaîne JSON des options disponibles dans le select (valeur et label) */
  @Prop() options: string;
  /** Taille du select, peut être "large" ou "medium" */
  @Prop() size: 'large' | 'medium' = 'medium';
  /** Permet la sélection multiple si vrai */
  @Prop() multiple: boolean = false;
  /** Active la fonction de recherche dans la liste des options */
  @Prop() isSearch: boolean = false;
  /** Texte d'assistance à afficher sous le champ */
  @Prop() assistiveText?: string;
  /** Message d'erreur à afficher sous le champ */
  @Prop() error?: string = '';
  /** Message d'indication (hint) à afficher sous le champ */
  @Prop() hint?: string = '';
  /** Message de succès à afficher sous le champ */
  @Prop() success?: string = '';
  /** Affiche une icône d'erreur si vrai */
  @Prop() showErrorIcon?: boolean = true;
  /** Affiche une icône d'indication si vrai */
  @Prop() showHintIcon?: boolean = true;
  /** Affiche une icône de succès si vrai */
  @Prop() showSuccessIcon?: boolean = true;

  @AttachInternals() internals: ElementInternals;

  componentWillLoad() {
    if (this.multiple) {
      if (typeof this.value === 'string') {
        try {
          this.internalValue = JSON.parse(this.value);
        } catch (e) {
          this.internalValue = [];
        }
      } else if (Array.isArray(this.value)) {
        this.internalValue = this.value;
      } else {
        this.internalValue = [];
      }
    } else {
      this.internalValue = this.value as string | number;
    }

    if (this.options) {
      this.parseOptions(this.options);
    }
    this.updateSelectedOption();
  }

  componentDidLoad() {
    document.addEventListener('click', this.handleClickOutside);
    this.internals.setFormValue(this.getFormValue());
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  private handleClickOutside = (event: MouseEvent) => {
    if (this.isOpen) {
      const target = event.composedPath()[0] as Node;
      const isInsideHost = this.host.contains(target);
      const searchField = this.host.shadowRoot?.querySelector('.spw-custom-select__search-input');

      const isInsideSearchField = searchField?.contains(target);

      if (!isInsideHost && !isInsideSearchField) {
        if (this.multiple) {
          const optionsContainer = this.host.shadowRoot?.querySelector('.spw-custom-select__options');
          if (optionsContainer && optionsContainer.contains(target)) {
            return;
          }
        }

        this.closeDropdown();
      }
    }
  };

  @Watch('options')
  parseOptions(newValue: string) {
    try {
      this.parsedOptions = JSON.parse(newValue).map(option => ({
        value: option.value,
        label: option.label,
      }));
      this.updateSelectedOption();
    } catch (e) {
      this.parsedOptions = [];
    }
  }

  @Watch('value')
  valueChangedHandler(newValue: (string | number)[] | string | number) {
    if (this.multiple) {
      this.internalValue = Array.isArray(newValue) ? newValue : [newValue];
    } else {
      this.internalValue = newValue;
    }
    this.updateSelectedOption();
    this.internals.setFormValue(this.getFormValue());
    this.valueChanged.emit(this.internalValue);
  }

  private updateSelectedOption() {
    this.internals.setFormValue(this.getFormValue());

    if (!this.multiple && this.internalValue !== undefined) {
      this.focusedOptionIndex = this.parsedOptions.findIndex(option => String(option.value) === String(this.internalValue));
    } else if (this.multiple && Array.isArray(this.internalValue)) {
      this.internalValue = (this.internalValue as (string | number)[])
        .filter(val => this.parsedOptions.some(option => String(option.value) === String(val)))
        .sort((a, b) => this.parsedOptions.findIndex(option => String(option.value) === String(a)) - this.parsedOptions.findIndex(option => String(option.value) === String(b)));
    }
  }

  @Watch('internalValue')
  internalValueChanged() {
    this.hasSelectedValue = Array.isArray(this.internalValue) ? this.internalValue.length > 0 : Boolean(this.internalValue);
  }

  private getFormValue(): string | null {
    if (this.multiple) {
      return Array.isArray(this.internalValue) && this.internalValue.length > 0 ? this.internalValue.map(v => String(v)).join(',') : null;
    }
    return this.internalValue ? String(this.internalValue) : null;
  }

  private toggleDropdown(event?: MouseEvent) {
    if (!this.disabled) {
      if (event) {
        const target = event.composedPath()[0] as HTMLElement;

        if (target.classList.contains('spw-custom-select__search-input')) {
          return;
        }

        event.stopPropagation();
      }
      this.determineDropdownDirection();
      this.isOpen = !this.isOpen;
      if (!this.isOpen) {
        this.focusedOptionIndex = null;
      }
    }
  }

  private determineDropdownDirection() {
    const rect = this.selectContainer.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    if (rect.bottom + 200 > viewportHeight && rect.top > 200) {
      this.dropdownDirection = 'up';
    } else {
      this.dropdownDirection = 'down';
    }
  }

  private openDropdownWithKeyboard() {
    this.determineDropdownDirection();
    this.isOpen = true;
    this.focusedOptionIndex = 0;
  }

  private closeDropdown() {
    this.isOpen = false;
    this.focusedOptionIndex = null;
    this.searchText = '';
  }

  private selectOption(optionValue: string | number, event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }

    if (this.multiple) {
      const index = (this.internalValue as (string | number)[]).findIndex(val => String(val) === String(optionValue));
      if (index > -1) {
        this.internalValue = [...(this.internalValue as (string | number)[]).slice(0, index), ...(this.internalValue as (string | number)[]).slice(index + 1)];
      } else {
        this.internalValue = [...(this.internalValue as (string | number)[]), optionValue];
      }
      this.internalValue = (this.internalValue as (string | number)[]).sort(
        (a, b) => this.parsedOptions.findIndex(option => String(option.value) === String(a)) - this.parsedOptions.findIndex(option => String(option.value) === String(b)),
      );
    } else {
      this.internalValue = optionValue;
      this.closeDropdown();
    }

    this.value = this.internalValue;
    this.internals.setFormValue(this.getFormValue());
    this.valueChanged.emit(this.internalValue);
    this.updateSelectedOption();
  }

  private handleOptionKeyDown(event: KeyboardEvent, optionValue: string | number) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectOption(optionValue);
    }
  }

  private handleKeyDown(event: KeyboardEvent) {
    if ((event.target as HTMLElement).classList.contains('spw-custom-select__search-input')) {
      return;
    }
    switch (event.key) {
      case ' ':
        event.preventDefault();
        if (!this.isOpen) {
          this.openDropdownWithKeyboard();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.isOpen) {
          this.focusedOptionIndex = this.focusedOptionIndex === null ? 0 : (this.focusedOptionIndex + 1) % this.parsedOptions.length;
        } else {
          this.openDropdownWithKeyboard();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.isOpen) {
          this.focusedOptionIndex =
            this.focusedOptionIndex === null ? this.parsedOptions.length - 1 : (this.focusedOptionIndex - 1 + this.parsedOptions.length) % this.parsedOptions.length;
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (this.isOpen && this.focusedOptionIndex !== null) {
          this.selectOption(this.parsedOptions[this.focusedOptionIndex].value);
        }
        break;
      case 'Escape':
        if (this.isOpen) {
          this.closeDropdown();
        }
        break;
    }
  }

  private renderErrorMessage() {
    if (this.error) {
      return (
        <spw-field-message variant="error" showIcon={this.showErrorIcon}>
          {this.error}
        </spw-field-message>
      );
    }
  }

  private renderHintMessage() {
    if (this.hint) {
      return (
        <spw-field-message variant="hint" showIcon={this.showHintIcon}>
          {this.hint}
        </spw-field-message>
      );
    }
  }

  private renderSuccessMessage() {
    if (this.success) {
      return (
        <spw-field-message variant="success" showIcon={this.showSuccessIcon}>
          {this.success}
        </spw-field-message>
      );
    }
  }

  private get elementClass(): { [className: string]: boolean } {
    return {
      'spw-custom-select': true,
      [`spw-custom-select--${this.size}`]: true,
      'spw-custom-select--is-error': !!this.error,
      'spw-custom-select--disabled': this.disabled,
      'spw-custom-select--is-opened': this.isOpen,
      'spw-custom-select--has-value': this.hasSelectedValue,
      'spw-custom-select--no-value': !this.hasSelectedValue,
      'spw-custom-select--is-searchable': this.isSearch,
      'spw-custom-select--not-searchable': !this.isSearch,
    };
  }

  private normalizeString(str: string): string {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  private getSelectedLabels(): string {
    if (this.multiple) {
      return Array.isArray(this.internalValue) && this.internalValue.length > 0
        ? (this.internalValue as (string | number)[])
            .map(val => this.parsedOptions.find(option => String(option.value) === String(val))?.label)
            .filter(Boolean)
            .join(', ')
        : null;
    } else {
      const selectedOption = this.parsedOptions.find(option => String(option.value) === String(this.internalValue));
      return selectedOption ? selectedOption.label : null;
    }
  }

  private getFilteredOptions() {
    const normalizedSearchText = this.normalizeString(this.searchText);
    return this.parsedOptions.filter(option => this.normalizeString(option.label).includes(normalizedSearchText));
  }

  private handleSearchInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchText = input.value;
    this.focusedOptionIndex = 0;

    if (!this.isOpen) {
      this.isOpen = true;
    }
  }

  private isSearchVisible(): boolean {
    if (!this.isSearch) return false;
    if (!this.hasSelectedValue && !this.isOpen) return true;
    if (this.isSearch && this.isOpen) return true;
    return this.isOpen;
  }

  private showPlaceholder(): boolean {
    if (this.isSearch) return false;
    if (this.hasSelectedValue) return false;
    return true;
  }

  render() {
    const filteredOptions = this.getFilteredOptions();
    const showPlaceholder = this.showPlaceholder();

    return (
      <div class={this.elementClass} ref={el => (this.host = el as HTMLElement)}>
        {this.label && <spw-field-label label={this.label} name={this.name} required={this.required}></spw-field-label>}
        {this.assistiveText && (
          <spw-field-message class="spw-tw-mb-2 -spw-tw-mt-1" variant="hint" showIcon={false}>
            {this.assistiveText}
          </spw-field-message>
        )}
        <div class="spw-custom-select__wrapper">
          <div
            class="spw-custom-select__container"
            id={this.name}
            ref={el => (this.selectContainer = el as HTMLElement)}
            onClick={event => this.toggleDropdown(event)}
            onKeyDown={event => this.handleKeyDown(event)}
            tabindex={this.disabled ? -1 : 0}
          >
            <div class="spw-custom-select__selected">
              {this.isSearchVisible() && (
                <div class="spw-custom-select__search">
                  <input class="spw-custom-select__search-input" placeholder="Entrez votre recherche" onInput={event => this.handleSearchInput(event)} value={this.searchText} />
                </div>
              )}
              {showPlaceholder && <span>{this.placeholder}</span>}
              {this.multiple && (this.internalValue as (string | number)[]).length > 0 && (
                <div class="spw-custom-select__selected-items">
                  {(this.internalValue as (string | number)[]).map(val => (
                    <span class="spw-custom-select__selected-item">
                      {this.parsedOptions.find(option => String(option.value) === String(val))?.label}
                      <spw-icon icon="fa-times" class="spw-custom-select__deselect-icon" onClick={event => this.selectOption(val, event)}></spw-icon>
                    </span>
                  ))}
                </div>
              )}
              {!this.multiple && <span>{this.getSelectedLabels()}</span>}
            </div>
          </div>
          <div class="spw-custom-select__arrow">
            <spw-icon icon={this.isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}></spw-icon>
          </div>
          {this.isOpen && (
            <div class={`spw-custom-select__options spw-custom-select__options--${this.dropdownDirection}`}>
              {!!filteredOptions.length && (
                <ul>
                  {filteredOptions.map((option, index) => (
                    <li
                      class={{
                        'spw-custom-select__option': true,
                        'spw-custom-select__option--selected': this.multiple
                          ? (this.internalValue as (string | number)[]).some(val => String(val) === String(option.value))
                          : option.value === this.internalValue,
                        'spw-custom-select__option--focused': index === this.focusedOptionIndex,
                      }}
                      onClick={() => this.selectOption(option.value)}
                    >
                      {this.multiple && (
                        <spw-checkbox
                          onKeyDown={event => this.handleOptionKeyDown(event, option.value)}
                          class="spw-tw-mr-2"
                          checked={(this.internalValue as (string | number)[]).some(val => String(val) === String(option.value))}
                        ></spw-checkbox>
                      )}
                      {option.label}
                    </li>
                  ))}
                </ul>
              )}
              {!filteredOptions.length && <p class="spw-custom-select__no-results">Pas de résultats pour cette recherche.</p>}
            </div>
          )}
        </div>
        {this.renderErrorMessage()}
        {this.renderHintMessage()}
        {this.renderSuccessMessage()}
      </div>
    );
  }
}
